import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface ArtistPainterTemplateProps {
    profile: PublicProfile
}

export function ArtistPainterTemplate({ profile }: ArtistPainterTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center overflow-hidden" style={{ backgroundColor: colors.colors.background }}>
                <div className="absolute inset-0">
                    <div
                        className="absolute top-0 left-0 w-full h-full"
                        style={{ backgroundImage: `linear-gradient(to bottom right, ${colors.colors.accent}33, ${colors.colors.secondary}33)` }}
                    />
                    <div className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: colors.colors.accent + '4D' }} />
                    <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: colors.colors.secondary + '4D' }} />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 md:px-6 md:py-24 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            {profile.tagline && (
                                <p
                                    className="uppercase tracking-widest mb-4 text-sm font-medium"
                                    style={{ color: colors.colors.secondary }}
                                >
                                    {profile.tagline}
                                </p>
                            )}
                            <h1
                                className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                Where <span style={{ color: colors.colors.accent }}>Color</span> Meets <span style={{ color: colors.colors.secondary }}>Emotion</span>
                            </h1>
                            <p className="text-xl mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {profile.bio || t('template.artist.abstractAndFigurative')}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <a
                                    href="#work"
                                    className="inline-flex items-center px-8 py-4 rounded-full font-semibold transition-all hover:opacity-90"
                                    style={{ backgroundColor: colors.colors.accent, color: colors.colors.background }}
                                >
                                    {t('template.artist.exploreWork')}
                                </a>
                                <a
                                    href="#contact"
                                    className="inline-flex items-center px-8 py-4 border-2 rounded-full font-semibold transition-all hover:opacity-90"
                                    style={{ borderColor: colors.colors.secondary, color: colors.colors.secondary }}
                                >
                                    {t('template.artist.inquire')}
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div
                                className="absolute inset-0 rounded-3xl transform rotate-3"
                                style={{ backgroundImage: `linear-gradient(135deg, ${colors.colors.accent}80, ${colors.colors.secondary}80)` }}
                            />
                            {profile.cover_image ? (
                                <img
                                    src={profile.cover_image}
                                    alt={profile.business_name}
                                    className="relative rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="relative rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="relative rounded-3xl shadow-2xl w-full aspect-square flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary + '20' }}
                                >
                                    <span className="text-6xl">🎨</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio/Gallery Section */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="work" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.artist.portfolio')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.artist.selectedWorks')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={6}
                            enableLightbox
                            showCaptions
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* About Section */}
            <section id="about" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.background }}>
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="relative">
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="rounded-3xl shadow-2xl w-full aspect-square flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary + '20' }}
                                >
                                    <span className="text-6xl">🎨</span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.background }}
                            >
                                <p className="text-4xl font-bold">15+</p>
                                <p className="text-sm">{t('template.artist.yearsCreating')}</p>
                            </div>
                        </div>
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.artist.aboutTheArtist')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.artist.theStoryBehindTheBrush')}
                            </h2>
                            {profile.bio && (
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                            )}
                            {profile.location && (
                                <p className="text-sm mb-6" style={{ color: colors.colors.textMuted }}>
                                    📍 {profile.location}
                                </p>
                            )}
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="flex items-center space-x-4">
                                    {profile.social_links.map((link) => (
                                        <a
                                            key={link.platform}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Follow on ${link.platform}`}
                                            className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                            style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                        >
                                            <span className="text-lg">
                                                {link.platform === 'instagram' && '📸'}
                                                {link.platform === 'twitter' && '🐦'}
                                                {link.platform === 'youtube' && '📺'}
                                                {!['instagram', 'twitter', 'youtube'].includes(link.platform) && '🔗'}
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services/Commissions Section */}
            {profile.services && profile.services.length > 0 && (
                <section id="commissions" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.text }}>
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.secondary }}
                            >
                                {t('template.artist.customWork')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white"
                                style={{ fontFamily: typography.heading }}
                            >
                                {t('template.artist.commissionAPiece')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {profile.services.map((service, index) => (
                                <div
                                    key={service.id}
                                    className="rounded-3xl p-8 border border-gray-700 transition-colors hover:border-current"
                                    style={{ borderColor: index === 0 ? colors.colors.accent : index === 1 ? colors.colors.secondary : '#FFB347' }}
                                >
                                    <div
                                        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                                        style={{ backgroundColor: (index === 0 ? colors.colors.accent : index === 1 ? colors.colors.secondary : '#FFB347') + '33' }}
                                    >
                                        <span style={{ color: index === 0 ? colors.colors.accent : index === 1 ? colors.colors.secondary : '#FFB347' }}>
                                            {index === 0 ? '🖼️' : index === 1 ? '🏠' : '🤝'}
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-3 text-white">
                                        {service.name}
                                    </h3>
                                    {service.description && (
                                        <p className="mb-6" style={{ color: '#9CA3AF' }}>
                                            {service.description}
                                        </p>
                                    )}
                                    <div className="border-t border-gray-700 pt-6">
                                        {service.price_display !== 'hidden' && service.price ? (
                                            <>
                                                <p className="text-sm mb-2" style={{ color: '#6B7280' }}>{t('template.artist.startingPrice')}</p>
                                                <p
                                                    className="text-3xl font-bold"
                                                    style={{ color: index === 0 ? colors.colors.accent : index === 1 ? colors.colors.secondary : '#FFB347' }}
                                                >
                                                    ${service.price.toFixed(0)}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm mb-2" style={{ color: '#6B7280' }}>{t('template.artist.contactForPricing')}</p>
                                                <p
                                                    className="text-3xl font-bold"
                                                    style={{ color: index === 0 ? colors.colors.accent : index === 1 ? colors.colors.secondary : '#FFB347' }}
                                                >
                                                    {t('template.artist.freeQuote')}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Availability Section */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.artist.studioHours')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.artist.visitTheStudio')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
                            <div className="grid md:grid-cols-2 gap-8 mb-8">
                                {profile.availability.slice(0, 7).map((day) => (
                                    <div
                                        key={day.day_of_week}
                                        className="flex justify-between items-center py-3 border-b border-gray-100"
                                    >
                                        <span className="font-semibold" style={{ color: colors.colors.text }}>
                                            {day.day_name}
                                        </span>
                                        <span style={{ color: colors.colors.textMuted }}>
                                            {day.is_available
                                                ? day.is_all_day
                                                    ? t('template.common.allDay')
                                                    : `${day.start_time} - ${day.end_time}`
                                                : t('template.common.byAppointment')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="text-center rounded-xl p-6" style={{ backgroundColor: colors.colors.accent + '1A' }}>
                                <p style={{ color: colors.colors.text }}>
                                    <span className="mr-2" style={{ color: colors.colors.accent }}>📍</span>
                                    {t('template.artist.studioVisitsByAppointment')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section id="contact" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.artist.getInTouch')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.artist.letsConnect')}
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {t('template.artist.collectingArtCommissioning')}
                            </p>
                            <div className="space-y-6">
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.accent + '1A' }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.artist.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary + '1A' }}
                                        >
                                            <span style={{ color: colors.colors.secondary }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.artist.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: '#FFB3471A' }}
                                        >
                                            <span style={{ color: '#FFB347' }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.artist.studio')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-semibold mb-4" style={{ color: colors.colors.text }}>{t('template.artist.followMyJourney')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-14 h-14 rounded-xl flex items-center justify-center transition-all hover:opacity-80"
                                                style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                            >
                                                <span className="text-xl">
                                                    {link.platform === 'instagram' && '📸'}
                                                    {link.platform === 'twitter' && '🐦'}
                                                    {link.platform === 'youtube' && '📺'}
                                                    {!['instagram', 'twitter', 'youtube'].includes(link.platform) && '🔗'}
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <form className="rounded-3xl p-8 shadow-xl" style={{ backgroundColor: colors.colors.background }}>
                                <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: typography.heading, color: colors.colors.text }}>
                                    {t('template.artist.sendAMessage')}
                                </h3>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.artist.name')}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-4 rounded-xl bg-white border border-gray-200 outline-none transition-all"
                                            placeholder={t('template.artist.yourName')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.artist.email')}
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-4 rounded-xl bg-white border border-gray-200 outline-none transition-all"
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    {profile.services && profile.services.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                                {t('template.artist.inquiryType')}
                                            </label>
                                            <select
                                                className="w-full px-4 py-4 rounded-xl bg-white border border-gray-200 outline-none transition-all cursor-pointer"
                                            >
                                                <option value="">{t('template.artist.selectInquiryType')}</option>
                                                {profile.services.map((service) => (
                                                    <option key={service.id} value={service.id}>
                                                        {service.name}
                                                    </option>
                                                ))}
                                                <option value="purchase">{t('template.artist.purchaseQuestion')}</option>
                                                <option value="exhibition">{t('template.artist.exhibitionInformation')}</option>
                                                <option value="general">{t('template.artist.generalQuestion')}</option>
                                            </select>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.artist.message')}
                                        </label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-4 py-4 rounded-xl bg-white border border-gray-200 outline-none transition-all resize-none"
                                            placeholder={t('template.artist.tellMeAboutYourInterest')}
                                        />
                                    </div>
                                    <button
                                        onClick={() => setIsBookingOpen(true)}
                                        className="w-full py-4 rounded-xl font-semibold text-center transition-colors hover:opacity-90"
                                        style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                    >
                                        {t('template.artist.bookNow')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 md:px-6 lg:px-8 text-center" style={{ backgroundColor: colors.colors.text }}>
                <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundImage: `linear-gradient(135deg, ${colors.colors.accent}, ${colors.colors.secondary})` }}
                >
                    <span className="text-2xl">🎨</span>
                </div>
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: typography.heading, color: colors.colors.background }}>
                    {profile.business_name}
                </h3>
                <p className="mb-6" style={{ color: '#9CA3AF' }}>
                    {t('template.artist.contemporaryArtist')}
                </p>
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Follow on ${link.platform}`}
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                style={{ backgroundColor: colors.colors.background + '1A', color: colors.colors.background }}
                            >
                                <span className="text-sm">
                                    {link.platform === 'instagram' && '📸'}
                                    {link.platform === 'twitter' && '🐦'}
                                    {!['instagram', 'twitter'].includes(link.platform) && '🔗'}
                                </span>
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm" style={{ color: '#6B7280' }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.artist.allRightsReserved')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}


export default ArtistPainterTemplate;
